import { useSyncExternalStore } from 'react';

import identity from 'lodash-es/identity';
import isEqual from 'lodash-es/isEqual';
import memoizeOne from 'memoize-one';

import FeatureGates from '@atlaskit/feature-gate-js-client';

const deepMemoized = memoizeOne(identity, isEqual);

const subscribe = (onStoreChange: () => void) => {
  return FeatureGates.onAnyUpdated(() => {
    onStoreChange();
  });
};

const getSnapshot = () => {
  return deepMemoized(FeatureGates.getOverrides().configs);
};

export const useExperimentsOverrides = () => {
  return useSyncExternalStore(subscribe, getSnapshot);
};

/**
 * These aliases aren't strictly necessary (we could use the `FeatureGates` methods directly in
 * our dev tools components), but they might help make refactoring easier if we decide to change
 * the way we read overrides in the future (like persisting them to the server via a cookie or
 * user preference) in order to support experiments provided by and/or used in Core or experiments
 * that are required during SSR.
 */

export const getExperiment = (name: string) => {
  return FeatureGates.getExperiment(name, { fireExperimentExposure: false });
};

export const clearAllOverrides = () => {
  return FeatureGates.clearAllOverrides();
};

export const clearConfigOverride = (name: string) => {
  return FeatureGates.clearConfigOverride(name);
};

export const isExperimentExists = (name: string) => {
  return FeatureGates.isExperimentExists(name);
};

export const overrideConfig = (
  name: string,
  value: Record<string, unknown>
) => {
  return FeatureGates.overrideConfig(name, value);
};
